import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Paginated PDF viewer for the "pdf" capture type - one page rendered at a
// time with prev/next controls, matching the "整个文件、可以分页预览"
// product choice (not split into per-page captures, not stitched into one
// long image).
export function PdfPreview({ pdfDataUrl, captureId, height }) {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  if (!pdfDataUrl) {
    return (
      <div className="aspect-[4/3] flex items-center justify-center bg-muted text-sm text-muted-foreground" data-testid={`pdf-preview-loading-${captureId}`}>
        {t("editDialog.pdfLoading")}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`pdf-preview-${captureId}`}>
      <div
        className={`border border-border bg-muted flex items-center justify-center overflow-auto ${height ? "" : "max-h-[70vh]"}`}
        style={height ? { height } : undefined}
      >
        <Document
          file={pdfDataUrl}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            setPageNumber(1);
          }}
          loading={<div className="p-8 text-sm text-muted-foreground">{t("editDialog.pdfLoading")}</div>}
          error={<div className="p-8 text-sm text-destructive">{t("editDialog.pdfLoadError")}</div>}
        >
          {/* Zoomed view (see CaptureEditPanel's zoom dialog) sizes the page
              by height instead of the default fixed 640px width, so the PDF
              fills the full screen height per user request. */}
          {height ? (
            <Page pageNumber={pageNumber} height={typeof height === "number" ? height : undefined} renderAnnotationLayer={false} renderTextLayer={false} />
          ) : (
            <Page pageNumber={pageNumber} width={640} renderAnnotationLayer={false} renderTextLayer={false} />
          )}
        </Document>
      </div>
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid={`pdf-preview-controls-${captureId}`}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => p - 1)}
            data-testid={`pdf-preview-prev-page-${captureId}`}
          >
            <CaretLeft size={14} />
          </Button>
          <span className="text-xs text-muted-foreground" data-testid={`pdf-preview-page-indicator-${captureId}`}>
            {t("editDialog.pdfPageIndicator", { current: pageNumber, total: numPages })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => p + 1)}
            data-testid={`pdf-preview-next-page-${captureId}`}
          >
            <CaretRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
}
